/* eslint-disable no-debugger */
import {DatePicker, Form, Input, Modal, Select, Spin, Switch, Tabs} from 'antd';
import {buildWalletAudience} from '../utils/walletAudience';
import {buildInvestmentListAudience} from '../utils/investmentAudience';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useCallback, useEffect} from 'react';
import styles from './newNotification.module.scss';
import backIcon from 'images/back.png';
import {useNavigate} from 'react-router-dom';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {
  GET_NOTIFICATION_CLASSES,
  NOTIFICATION_BATCH_QUERY,
} from 'graphql/queries/notifications';
import {
  GET_SCHEDULED_NOTIFICATIONS,
  SEND_NOTIFICATION_TO_ALL_USERS,
  SEND_NOTIFICATION_TO_USERS,
} from 'graphql/mutations/notifications';
import {toast} from 'react-toastify';
import moment from 'moment';
import _ from 'lodash';
import {
  assetClassEnum,
  emailNotificationTypes,
  notificationLanguages,
  notificationOptions,
  notificationTypes,
} from 'utils/constants';
import PushNotificationData from './pushNotificationData/PushNotificationData';
import {RelatedAnnouncementPicker} from '../components/RelatedAnnouncementPicker';
import EmailNotificationFields from './emailNotificationFields/EmailNotificationFields';
import {GET_ALL_COUNTRIES} from 'graphql/queries/users';
import NotificationFilter from './notificationFilter';
import {Button} from '@dabafinance/react-components';
import {WhatsappNotificationFields} from './whatsappNotificationFields/whatsappNotificationFields';
import ReactQuill from 'react-quill';
import {RichTextEditor} from 'components/richTextEditor';
import {onTranslateNotification} from './actions';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';
import {disclosureTypeEnum} from 'utils/mock';
import {GET_ALL_EMAIL_DISCLOSURES} from 'graphql/queries/emailDisclosure';
import {getUserTimezone} from 'utils/helper';
import {useNotificationForm} from '../hooks/useNotificationForm';
import {
  buildRecipientPayload,
  countRecipients,
} from 'components/notificationRecipientSelect/recipientCsv';
import {
  MIN_EXPIRY_HOURS,
  MIN_LEAD_MINUTES,
  buildWhatsappVariables,
  earliestSendDate,
  htmlToPlainText,
  transformFormDataToNotificationPayload,
} from '../utils/notificationDataTransformers';

const NewNotification = ({
  initialNotification = null,
  isEditMode = false,
  onSubmit: customOnSubmit = null,
  submitButtonLabel = 'Send Notification',
  disableButton = false,
  pageTitle = 'New Notification',
}) => {
  // Use the reusable hook for form state management
  const formState = useNotificationForm(initialNotification, isEditMode);
  const {
    form,
    // Form field values (from Form.useWatch)
    category,
    notificationCategory,
    type,
    title,
    body,
    emailBody,
    emailHeader,
    emailImage,
    addDisclosure,
    disclosureType,
    frequency,
    date,
    audience,
    baseLanguage,
    linkShouldOpenInTheApp,
    // UI state
    activeTab,
    setActiveTab,
    emailPreviewImage,
    setEmailPreviewImage,
    // Metadata state
    opportunityMetaData,
    setOpportunityMetaData,
    reportMetaData,
    setReportMetaData,
    companyMetaData,
    setCompanyMetaData,
    groupMetaData,
    setGroupMetaData,
    recipients,
    setRecipients,
    defaultUserIds,
    // Translation state
    translatedLanguage,
    setTranslatedLanguage,
    enableChangeTranslation,
    setEnableChangeTranslation,
    openDeleteTranslationModal,
    setOpenDeleteTranslationModal,
    selectedTranslation,
    setSelectedTranslation,
    // WhatsApp state
    selectedWhatsappTemplate,
    setSelectedWhatsappTemplate,
  } = formState;

  const isAppOrEmail = activeTab === 'APP' || activeTab === 'EMAIL';

  /**
   * The in-app feed renders HTML, so its body gets the same rich editor and
   * image upload as an announcement's popup content.
   *
   * Push does not: FCM carries a plain string, and the 115-character cap on
   * this field exists for it. The App tab covers both channels, so the rich
   * editor appears only when IN_APP is selected *without* PUSH — a body shared
   * with a push would arrive as raw markup on the device.
   */
  const isInAppOnly =
    activeTab === 'APP' &&
    (notificationCategory ?? []).includes('IN_APP') &&
    !(notificationCategory ?? []).includes('PUSH');

  const {data: getAllCountries} = useQuery(GET_ALL_COUNTRIES);
  const [getNotificationClass, {data: getNotificationClasses}] = useLazyQuery(
    GET_NOTIFICATION_CLASSES,
  );
  const [getEmailDisclosures, {data: getAllEmailDisclosures}] = useLazyQuery(
    GET_ALL_EMAIL_DISCLOSURES,
  );

  useEffect(() => {
    if (isEditMode && initialNotification) {
      const notification = Array.isArray(initialNotification)
        ? initialNotification[0]
        : initialNotification;

      if (notification?.category) {
        const categories = Array.isArray(notification.category)
          ? notification.category
          : [notification.category];

        let channels = categories;
        if (categories.includes('EMAIL')) {
          channels = ['EMAIL'];
        } else if (categories.includes('WHATSAPP')) {
          channels = ['WHATSAPP'];
        }

        getNotificationClass({
          variables: {
            input: {
              active: true,
              allOrNone: true,
              channels,
            },
          },
        });
      }
    }
  }, [initialNotification, isEditMode, getNotificationClass]);

  const onDisclosureType = e => {
    form.setFieldsValue({disclosureType: e});
    getEmailDisclosures({
      variables: {
        disclosureType: e,
        limit: 100,
      },
    });
  };

  const onCategoryChange = useCallback(
    e => {
      form.setFieldsValue({category: e});

      // Adding PUSH turns the body back into a plain string. Anything written
      // in the rich editor would otherwise ship to the device as raw markup,
      // so the tags are stripped and the push length cap re-applied.
      if (e.includes('PUSH')) {
        const current = form.getFieldValue('body');
        if (typeof current === 'string' && /<[^>]+>/.test(current)) {
          const plain = htmlToPlainText(current);
          form.setFieldsValue({body: plain.slice(0, 115)});
          toast.info(
            'Push messages are plain text, so the formatting was removed from the message.',
          );
        }
      }

      getNotificationClass({
        variables: {
          input: {
            active: true,
            allOrNone: true,
            // eslint-disable-next-line no-undef
            channels: Array.from(new Set([...notificationCategory, ...e])),
          },
        },
      });
    },
    [notificationCategory, getNotificationClass, form],
  );

  const onTabChange = e => {
    setActiveTab(e);
    getNotificationClass({
      variables: {
        input: {
          active: true,
          allOrNone: true,
          channels: [e],
        },
      },
    });
  };
  /* The below code is filtering an array called `notificationTypes` based on certain conditions.
    1. Push Option was select,
    2. Only then is External Link Available
    3. if we have more than one category, it is filtered out
  */
  const filteredNotificationTypes = category.includes('SMS')
    ? notificationTypes.filter(data => data.value !== 'External Link')
    : notificationTypes.slice();

  const {Option} = Select;
  const {TextArea} = Input;
  const navigate = useNavigate();

  const {data, loading} = useQuery(NOTIFICATION_BATCH_QUERY);

  const [sendNotificationToAllUsers, {loading: loadingRequest}] = useMutation(
    SEND_NOTIFICATION_TO_ALL_USERS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );
  const [sendNotificationToUsers, {loading: loadingUsersRequest}] = useMutation(
    SEND_NOTIFICATION_TO_USERS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );
  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {TabPane} = Tabs;

  const handleSendNotification = formValues => {
    // If custom onSubmit is provided (for update mode), use it
    if (customOnSubmit && isEditMode) {
      if (
        formValues?.audience === 'USERS' &&
        countRecipients(recipients) === 0
      ) {
        return toast.error(
          'Select at least one recipient, or upload a CSV of email addresses.',
        );
      }
      const payload = transformFormDataToNotificationPayload(formValues, {
        ...formState,
        activeTab,
        date,
        frequency,
      });
      return customOnSubmit(payload);
    }
    /**
     * Schedule fields optional
     */

    const scheduleFields = {};
    // Both fallbacks used to land inside the five-minute window the backend
    // enforces, so a form that looked valid was refused on submit.
    if (date) {
      const chosen = moment(date);
      scheduleFields.date = chosen.isBefore(earliestSendDate())
        ? earliestSendDate().toISOString()
        : chosen.toISOString();
    } else {
      scheduleFields.date = earliestSendDate().toISOString();
    }
    if (!date) scheduleFields.frequency = 'ONE_TIME';
    if (formValues?.frequency) scheduleFields.frequency = formValues?.frequency;
    if (formValues?.selectedDays && formValues?.selectedDays?.length)
      scheduleFields.selectedDays = formValues?.selectedDays;

    if (formValues?.expires) {
      scheduleFields.expires = new Date(formValues?.expires).toISOString();
    }

    /**
     * Filtering by either test or live campaign Optional
     */
    const optionalFields = {};
    if (formValues?.audience === 'INVESTORS_IN_A_CAMPAIGN') {
      optionalFields.campaign = formValues?.liveCampaignId;
    }
    if (formValues?.audience === 'INVESTORS_WITH_RESERVATIONS') {
      optionalFields.campaign = formValues?.testCampaignId;
    }
    if (formValues?.externalLink) {
      optionalFields.externalLink = formValues?.externalLink;
      optionalFields.shouldOpenInApp = linkShouldOpenInTheApp;
    }

    let signupFilter = {};
    if (formValues?.signupDate === 'RANGE') {
      signupFilter.signupDateRange = {
        from: formValues?.from
          ? new Date(formValues?.from).toISOString()
          : null,
        to: formValues?.to ? new Date(formValues?.to).toISOString() : null,
      };
    }
    if (formValues?.signupDate === 'DURATION') {
      signupFilter.signupDateDuration = {
        value: Number(formValues?.durationValue),
        unit: formValues?.durationUnit,
      };
    }

    /**
     * Inclusion of optional Email Fields
     */
    if (emailHeader || formValues?.emailHeader) {
      optionalFields.emailHeader = formValues?.emailHeader || emailHeader;
    }
    if (emailImage || formValues?.emailBanner) {
      optionalFields.emailBanner = formValues?.emailBanner || emailImage;
    }
    if (formValues?.type?.length) {
      optionalFields.type = formValues?.type;
    }

    /**
     * ASSET CLASS NOTIFICATION
     *
     */
    let orderOptionalFields = {};
    if (formValues.orderFrom || formValues.orderTo) {
      orderOptionalFields.orderTransactionDate = {
        from: formValues.orderFrom
          ? new Date(formValues.orderFrom).toISOString()
          : null,
        to: formValues.orderTo
          ? new Date(formValues.orderTo).toISOString()
          : null,
      };
    }
    if (formValues?.orderStatus?.length)
      orderOptionalFields.orderStatus = formValues?.orderStatus;
    Object.assign(orderOptionalFields, buildInvestmentListAudience(formValues));
    if (formValues?.assetId) orderOptionalFields.assetId = formValues?.assetId;
    if (formValues?.isOrderOnHold)
      orderOptionalFields.isOrderOnHold = formValues?.isOrderOnHold;

    // AML, Document Check, KYC
    if (formValues?.isAmlApproved)
      orderOptionalFields.isAmlApproved = formValues?.isAmlApproved;
    if (formValues?.isDocumentCheckApproved)
      orderOptionalFields.isDocumentCheckApproved =
        formValues?.isDocumentCheckApproved;
    if (formValues?.isKycApproved)
      orderOptionalFields.isKycApproved = formValues?.isKycApproved;
    if (formValues?.riskProfile)
      orderOptionalFields.riskProfile = formValues?.riskProfile;

    const whatsappVariables =
      activeTab === 'WHATSAPP'
        ? buildWhatsappVariables(formValues, selectedWhatsappTemplate)
        : {};
    /**
     * PUSH NOTIFICATIONS METADATA
     */
    let metaData = {};
    if (!_.isEmpty(companyMetaData)) metaData.company = companyMetaData;
    else if (!_.isEmpty(reportMetaData)) metaData.report = reportMetaData;
    else if (!_.isEmpty(opportunityMetaData)) {
      metaData.opportunity = opportunityMetaData;
    } else if (!_.isEmpty(groupMetaData)) metaData.group = groupMetaData;

    // Send Notification to specific User(s)
    if (formValues?.audience === 'USERS') {
      // Caught here rather than by the backend: its own "You need to select at
      // least one user" only comes back after a round trip, and every other
      // validation failure on this mutation is flattened into an unactionable
      // generic message.
      if (countRecipients(recipients) === 0) {
        return toast.error(
          'Select at least one recipient, or upload a CSV of email addresses.',
        );
      }

      return sendNotificationToUsers({
        variables: {
          data: {
            category: activeTab === 'EMAIL' ? 'EMAIL' : formValues?.category,
            title: formValues?.title,
            draft: formValues?.draft,
            body:
              activeTab === 'EMAIL'
                ? formValues?.emailBody || emailBody
                : formValues?.body || body,
            groups: formValues?.groups,
            // `userIds` and `userEmails`, unioned. The service resolves the
            // addresses against existing users and notifies anyone named by
            // both only once.
            ...buildRecipientPayload(recipients),
            from: formValues?.senderEmail,
            replyTo: formValues?.replyEmail,
            addDisclosure,
            class: formValues?.class,
            // Deprecated and rejected at runtime on dev; the destination is
            // the instrument itself.
            ...(formValues.instrumentId && {
              instrumentId: formValues.instrumentId,
            }),
            // DIVIDENDS_ANNOUNCEMENT is outside the v2 matrix and still needs
            // its two legacy fields; scoped so no other route sends them.
            ...(formValues.type === 'DIVIDENDS_ANNOUNCEMENT' && {
              ...(formValues.assetClass && {
                assetClass: formValues.assetClass,
              }),
              ...(formValues.company && {
                company: formValues.company.split(';')[1],
              }),
            }),
            disclosure: formValues?.disclosure,
            ...(formValues.collectionId && {
              collectionId: formValues.collectionId,
            }),
            // Optional announcement reference. Sent top-level — the service
            // emits it to mobile as `metadata.announcementID`, and the top
            // level wins if both shapes are present.
            ...(formValues.announcementId && {
              announcementId: formValues.announcementId,
            }),
            disclosureType: formValues?.disclosureType,
            metadata: {
              ...metaData,
            },
            ...optionalFields,
            topics: formValues?.topics?.map(data => data.split(';')[1]),
            schedule: {
              ...scheduleFields,
            },
            language: formValues?.baseLanguage,
            multilang:
              translatedLanguage.length > 0
                ? translatedLanguage.map(item => {
                    return {
                      body: item?.message,
                      title: item?.title,
                      language: item?.language,
                      emailHeader: item?.header ? item?.header : undefined,
                    };
                  })
                : [],
            audience: {
              ...signupFilter,
              ...orderOptionalFields,
              ...buildWalletAudience(formValues),
              state: formValues?.state,
              city: formValues?.city,
              citizenStatus: formValues?.citizenStatus,
              countryOfBirth:
                formValues?.countryOfBirth?.length > 0
                  ? formValues?.countryOfBirth?.map(item => item.split(';')[1])
                  : formValues?.countryOfBirth,
              countryOfCitizenship:
                formValues?.countryOfCitizenship?.length > 0
                  ? formValues?.countryOfCitizenship.map(
                      item => item.split(';')[1],
                    )
                  : formValues?.countryOfCitizenship,
              country: formValues?.countryOfResidence,
              visaType: formValues?.visaType,
              investmentHorizon: formValues?.investmentHorizon,
              annualNetIncome: formValues?.netIncome,
              employmentStatus: formValues?.employmentStatus,
              taxFilingStatus: formValues?.taxFilingStatus,
              estimatedNetWorth: formValues?.estimatedNetWorth,
              investmentPreference: formValues?.investmentPreference,
              investmentExperience: formValues?.investmentExperience,
              recommendedPortfolioStrategy: formValues?.investmentStrategies,
              affiliations: formValues?.investmentAffiliation,
              investmentGoals: formValues?.investmentGoals,
            },
            ...whatsappVariables,
          },
        },
        refetchQueries: [
          {
            query: GET_SCHEDULED_NOTIFICATIONS,
            variables: {
              data: {
                isActive: 'ACTIVE',
                isScheduled: true,
                status: 'PENDING',
              },
            },
          },
        ],
      })
        .then(({data: {sendNotificationToUsers}}) => {
          if (sendNotificationToUsers.__typename === 'Error') {
            toast.error(sendNotificationToUsers.message);
          } else {
            toast.success('Notification sent Successfully');
            navigate('/dashboard/notifications');
          }
        })
        .catch(err => toast.error(err));
    }

    // Send Notification to all users

    sendNotificationToAllUsers({
      variables: {
        data: {
          category: activeTab === 'EMAIL' ? 'EMAIL' : formValues?.category,
          title: formValues?.title,
          draft: formValues?.draft,
          body:
            activeTab === 'EMAIL'
              ? formValues?.emailBody || emailBody
              : formValues?.body || body,
          groups: formValues?.groups,
          from: formValues?.senderEmail,
          replyTo: formValues?.replyEmail,
          addDisclosure,
          class: formValues?.class,
          ...(formValues.instrumentId && {
            instrumentId: formValues.instrumentId,
          }),
          // DIVIDENDS_ANNOUNCEMENT is outside the v2 matrix and still needs
          // its two legacy fields; scoped so no other route sends them.
          ...(formValues.type === 'DIVIDENDS_ANNOUNCEMENT' && {
            ...(formValues.assetClass && {assetClass: formValues.assetClass}),
            ...(formValues.company && {
              company: formValues.company.split(';')[1],
            }),
          }),
          disclosure: formValues?.disclosure,
          disclosureType: formValues?.disclosureType,
          ...(formValues.collectionId && {
            collectionId: formValues.collectionId,
          }),
          // Optional announcement reference; see the note above.
          ...(formValues.announcementId && {
            announcementId: formValues.announcementId,
          }),
          metadata: {
            ...metaData,
          },
          ...optionalFields,
          topics: formValues?.topics?.map(data => data.split(';')[1]),
          schedule: {
            ...scheduleFields,
          },
          language: formValues?.baseLanguage,
          multilang:
            translatedLanguage.length > 0
              ? translatedLanguage.map(item => {
                  return {
                    body: item?.message,
                    title: item?.title,
                    language: item?.language,
                    emailHeader: item?.header ? item.header : undefined,
                  };
                })
              : [],
          audience: {
            ...signupFilter,
            ...orderOptionalFields,
            ...buildWalletAudience(formValues),
            category: formValues?.audience,
            accredited: formValues?.accredited,
            lastAppVisit: formValues?.latestVisit,
            isKycApproved: formValues?.isKycApproved,
            isDabaPro: formValues?.isDabaPro,
            verifiedInvestors: formValues?.verifiedInvestors,
            activeInvestor: formValues?.activeInvestors,
            profileCompletion: formValues?.profileCompletion,
            state: formValues?.state,
            city: formValues?.city,
            citizenStatus: formValues?.citizenStatus,
            countryOfBirth:
              formValues?.countryOfBirth?.length > 0
                ? formValues?.countryOfBirth?.map(item => item.split(';')[1])
                : formValues?.countryOfBirth,
            countryOfCitizenship:
              formValues?.countryOfCitizenship?.length > 0
                ? formValues?.countryOfCitizenship?.map(
                    item => item.split(';')[1],
                  )
                : formValues?.countryOfCitizenship,
            country: formValues?.countryOfResidence,
            visaType: formValues?.visaType,
            investmentHorizon: formValues?.investmentHorizon,
            annualNetIncome: formValues?.netIncome,
            employmentStatus: formValues?.employmentStatus,
            taxFilingStatus: formValues?.taxFilingStatus,
            estimatedNetWorth: formValues?.estimatedNetWorth,
            investmentPreference: formValues?.investmentPreference,
            investmentExperience: formValues?.investmentExperience,
            recommendedPortfolioStrategy: formValues?.investmentStrategies,
            affiliations: formValues?.investmentAffiliation,
            investmentGoals: formValues?.investmentGoals,
          },
          ...whatsappVariables,
        },
      },
      refetchQueries: [
        {
          query: GET_SCHEDULED_NOTIFICATIONS,
          variables: {
            data: {
              isActive: 'ACTIVE',
              isScheduled: true,
              status: 'PENDING',
            },
          },
        },
      ],
    })
      .then(({data: {sendNotificationToAllUsers}}) => {
        if (sendNotificationToAllUsers.__typename === 'Error') {
          toast.error(sendNotificationToAllUsers.message);
        } else {
          toast.success('Notification sent Successfully');
          navigate('/dashboard/notifications');
        }
      })
      .catch(err => toast.error(err));
  };

  const languageToBeTranslatedTo = notificationLanguages.filter(
    key => key.key !== baseLanguage,
  );

  const handleTranslation = async () => {
    if (!selectedTranslation) return;
    const translateProps = {
      sourceLang: baseLanguage,
      targetLang: selectedTranslation,
      targetText1: title,
      targetText2: activeTab === 'APP' ? body : emailBody,
    };
    if (activeTab === 'EMAIL') {
      translateProps.targetText3 = emailHeader;
    }
    try {
      const newValues = [...translatedLanguage];

      //Check if translation exists
      if (newValues.length && newValues[0]) {
        // Check if we're trying to translate in the same language
        const languageExists = newValues.some(
          data => data.language === selectedTranslation,
        );
        if (languageExists) {
          const updateTranslation = await onTranslateNotification(
            translateText,
            translateProps,
            activeTab,
          );
          const translationIndex = newValues.findIndex(
            data => data.language === selectedTranslation,
          );
          newValues[translationIndex].title = updateTranslation.title;
          newValues[translationIndex].message = updateTranslation.message;
          newValues[translationIndex].header = updateTranslation?.header;
          newValues[translationIndex].language = updateTranslation.language;
          setTranslatedLanguage(newValues);
        }
      } else {
        const triggerTranslation = await onTranslateNotification(
          translateText,
          translateProps,
          activeTab,
        );
        if (!triggerTranslation) {
          return toast.error('Something went wrong when Translating');
        }
        newValues.push(triggerTranslation);
        setTranslatedLanguage(newValues);
        setTranslatedLanguage(newValues);
      }
    } catch (error) {
      toast.error(`Something went wrong! Error: ${error}`);
    }
  };

  const handleChangeTranslatedValues = (key, event, name) => {
    let eventName = name;

    let eventValue = name ? event : event.target.value;
    const newFormValues = translatedLanguage.map((item, index) => {
      if (index === key) {
        return {...item, [eventName]: eventValue};
      }
      return item;
    });
    newFormValues[key][eventName] = eventValue;
    setTranslatedLanguage(newFormValues);
  };

  useEffect(() => {
    // Reset dynamic whatsapp template link
    form.setFieldValue('template-link-id', '');
  }, [selectedWhatsappTemplate]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  const onDeleteTab = e => {
    e.stopPropagation();
    setTranslatedLanguage([]);
    setOpenDeleteTranslationModal(false);
  };

  /* The above code is defining a function called `disableSendButton`. This function checks several
  conditions using logical operators (`||` and `&&`) and returns a boolean value. */
  const disableSendButton =
    loadingRequest || loadingUsersRequest || disableButton;
  return (
    <div className={styles.page}>
      <button
        type="button"
        className={styles.back}
        onClick={() => navigate(-1)}>
        <img src={backIcon} alt="" />
        Back
      </button>

      <div className={styles.pageHead}>
        <h1>{pageTitle}</h1>
        <p>
          Choose a channel, write the message, pick where it takes the reader,
          then who receives it and when.
        </p>
      </div>

      {/* Switching channel calls form.resetFields(), so this is a mode switch
          rather than a filter — the note below says so out loud. */}
      <div className={styles.channels}>
        <button
          type="button"
          className={`${styles.channel} ${
            activeTab === 'APP' ? styles.channelOn : ''
          }`}
          onClick={() => {
            setActiveTab('APP');
            form.resetFields();
            form.setFieldsValue({emailBody: ''});
          }}>
          Push &amp; in-app
        </button>
        <button
          type="button"
          className={`${styles.channel} ${
            activeTab === 'EMAIL' ? styles.channelOn : ''
          }`}
          onClick={() => {
            form.resetFields();
            onTabChange('EMAIL');
          }}>
          Email
        </button>
        {/* A stray literal "*" used to render between these two buttons. */}
        <button
          type="button"
          className={`${styles.channel} ${
            activeTab === 'WHATSAPP' ? styles.channelOn : ''
          }`}
          onClick={() => {
            form.resetFields();
            onTabChange('WHATSAPP');
            form.setFieldsValue({category: ['WHATSAPP']});
          }}>
          WhatsApp
        </button>
      </div>
      <p className={styles.channelNote}>Changing channel clears the form.</p>

      <div>
        <Form form={form} onFinish={handleSendNotification} layout="vertical">
          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionStep}>1</span>
              <h3>Basics</h3>
              <span>Language, delivery channels and notification class</span>
            </div>
            <div className={styles.sectionBody}>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: 'Base Language is required',
                  },
                ]}
                label="Base Language"
                className={styles.inputField}
                name="baseLanguage">
                <Select placeholder="Select a Language" name="baseLanguage">
                  {notificationLanguages.map(name => (
                    <Option key={name.key}>{name.label}</Option>
                  ))}
                </Select>
              </Form.Item>
              {activeTab === 'APP' && (
                <Form.Item
                  rules={[
                    {
                      required: true,
                      message: 'Category is required',
                    },
                  ]}
                  label="Category"
                  name="category"
                  className={styles.inputField}>
                  <Select
                    mode="multiple"
                    value={notificationCategory}
                    onChange={e => onCategoryChange(e)}
                    placeholder="Select a category"
                    name="category">
                    <Option value="PUSH">Push</Option>
                    <Option value="IN_APP">In App</Option>
                  </Select>
                </Form.Item>
              )}
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: 'Notification Class is required',
                  },
                ]}
                name="class"
                label="Notification Class"
                className={styles.inputField}>
                <Select placeholder="Select a class" name="class">
                  {getNotificationClasses?.getNotificationClasses?.data?.map(
                    item => (
                      <Option key={item?.id} value={item?.id}>
                        {item?.name}
                      </Option>
                    ),
                  )}
                </Select>
              </Form.Item>
              {activeTab === 'WHATSAPP' && (
                <WhatsappNotificationFields
                  selectedWhatsappTemplate={selectedWhatsappTemplate}
                  inputClassName={styles.inputField}
                  previewClassName={styles.whatsappPreview}
                  hiddenFieldClassName={styles.hiddenField}
                  onSelectedWhatsappTemplateChange={setSelectedWhatsappTemplate}
                  form={form}
                  languageToBeTranslatedTo={languageToBeTranslatedTo}
                  baseLanguage={baseLanguage}
                />
              )}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionStep}>2</span>
              <h3>Message</h3>
              <span>What the reader sees, and any translations</span>
            </div>
            <div className={styles.sectionBody}>
              {isAppOrEmail && (
                <Form.Item
                  label="Translation Language"
                  className={styles.inputField}
                  name="translatedLanguage">
                  <Select
                    placeholder="Select a Language"
                    allowClear
                    showSearch
                    value={selectedTranslation}
                    loading={loadingTranslation}
                    onChange={setSelectedTranslation}
                    name="type">
                    {languageToBeTranslatedTo.map(({label, key}) => (
                      <Option key={key}>{label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Tabs
                tabBarExtraContent={
                  <Button
                    disabled={loadingTranslation || !selectedTranslation}
                    label="Translate"
                    onClick={handleTranslation}
                  />
                }>
                {isAppOrEmail && (
                  <TabPane key="Original" tab="Base Language">
                    {isAppOrEmail && (
                      <>
                        <Form.Item
                          rules={[
                            {
                              required: true,
                              message: 'Title is required',
                            },
                          ]}
                          name="title"
                          label="Title"
                          className={styles.inputField}>
                          <Input placeholder="Title of the Notification" />
                        </Form.Item>
                      </>
                    )}
                    {activeTab === 'APP' && (
                      <div>
                        <Form.Item
                          rules={[
                            {
                              required: true,
                              message: isInAppOnly
                                ? 'Message is required'
                                : 'Message is required, Maximum of 115 characters',
                              // The cap is a push constraint; in-app has none.
                              ...(isInAppOnly ? {} : {max: 115}),
                            },
                          ]}
                          name="body"
                          label="Message"
                          tooltip={
                            isInAppOnly
                              ? 'Rich text body for the in-app feed. Inserted images upload to our storage rather than being embedded as base64.'
                              : undefined
                          }
                          className={styles.inputField}>
                          {isInAppOnly ? (
                            <RichTextEditor
                              withImage
                              className={styles.contentQuill}
                              placeholder="Write the in-app message…"
                            />
                          ) : (
                            <TextArea rows={6} placeholder="Type Message" />
                          )}
                        </Form.Item>
                      </div>
                    )}
                    {activeTab === 'EMAIL' && (
                      <EmailNotificationFields
                        modules={NewNotification.modules}
                        formats={NewNotification.format}
                        onEmailBodyChange={value =>
                          form.setFieldsValue({emailBody: value})
                        }
                        emailBody={emailBody}
                        header={emailHeader}
                        onHeaderChange={value =>
                          form.setFieldsValue({emailHeader: value})
                        }
                        onChangeImgUrl={value => {
                          form.setFieldsValue({emailBanner: value});
                          if (value) {
                            setEmailPreviewImage(value);
                          }
                        }}
                        emailPreviewImage={emailPreviewImage}
                        onChangeEmailPreviewImage={value => {
                          setEmailPreviewImage(value);
                          if (value) {
                            form.setFieldsValue({emailBanner: value});
                          }
                        }}
                      />
                    )}
                  </TabPane>
                )}
                {translatedLanguage.length > 0 &&
                  translatedLanguage.map((item, index) => (
                    <TabPane
                      tab={
                        <div className={styles.deleteTranslation}>
                          {
                            notificationLanguages.find(
                              lang => lang.key === item.language,
                            ).label
                          }
                          <p
                            role="button"
                            onClick={() => setOpenDeleteTranslationModal(true)}>
                            X
                          </p>
                        </div>
                      }
                      key={index}>
                      <div className={styles.updateButton}>
                        <Button
                          onClick={() =>
                            setEnableChangeTranslation(!enableChangeTranslation)
                          }
                          type="secondary"
                          label={
                            enableChangeTranslation
                              ? 'Edit Translation'
                              : 'Disable Translation'
                          }
                        />
                      </div>
                      <div key={index}>
                        {isAppOrEmail && (
                          <Form.Item
                            label="Title"
                            className={styles.inputField}>
                            <Input
                              placeholder="Title of the Notification"
                              value={item.title}
                              disabled={enableChangeTranslation}
                              onChange={e =>
                                handleChangeTranslatedValues(
                                  index,
                                  e.target.value,
                                  'title',
                                )
                              }
                            />
                          </Form.Item>
                        )}
                        {!!item.header && (
                          <Form.Item
                            label="Email Header"
                            className={styles.inputField}>
                            <Input
                              placeholder="Email Header"
                              disabled={enableChangeTranslation}
                              value={item.header}
                              onChange={e =>
                                handleChangeTranslatedValues(
                                  index,
                                  e.target.value,
                                  'header',
                                )
                              }
                            />
                          </Form.Item>
                        )}
                        {activeTab === 'EMAIL' && (
                          <div className={styles['quill-container']}>
                            <span>Email Body</span>
                            <ReactQuill
                              // Never undefined — see the emailBody field.
                              value={item.message || ''}
                              modules={NewNotification.modules}
                              formats={NewNotification.format}
                              className={styles.quill}
                              onChange={e =>
                                handleChangeTranslatedValues(
                                  index,
                                  e,
                                  'message',
                                )
                              }
                              readOnly={enableChangeTranslation}
                              placeholder="Message"
                            />
                          </div>
                        )}
                        {activeTab === 'APP' && (
                          <Form.Item
                            label="Message"
                            className={styles.inputField}>
                            {/* The translation must match the base language's
                            content type, or one language arrives as markup. */}
                            {isInAppOnly ? (
                              <RichTextEditor
                                withImage
                                className={styles.contentQuill}
                                placeholder="Translated in-app message…"
                                value={item.message}
                                onChange={value =>
                                  handleChangeTranslatedValues(
                                    index,
                                    value,
                                    'message',
                                  )
                                }
                              />
                            ) : (
                              <TextArea
                                value={item.message}
                                rows={6}
                                placeholder="Type Message"
                                disabled={enableChangeTranslation}
                                onChange={e =>
                                  handleChangeTranslatedValues(
                                    index,
                                    e.target.value,
                                    'message',
                                  )
                                }
                              />
                            )}
                          </Form.Item>
                        )}
                      </div>
                    </TabPane>
                  ))}
              </Tabs>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionStep}>3</span>
              <h3>Destination</h3>
              <span>Where tapping the notification takes the reader</span>
            </div>
            <div className={styles.sectionBody}>
              {isAppOrEmail && (
                <Form.Item
                  rules={[
                    {
                      required: activeTab === 'APP',
                      message: 'Type is required',
                    },
                  ]}
                  label="Type"
                  className={styles.inputField}
                  name="type">
                  <Select placeholder="Select a type" showSearch name="type">
                    {activeTab === 'APP'
                      ? filteredNotificationTypes.map(name => (
                          <Option key={name.label}>{name.value}</Option>
                        ))
                      : emailNotificationTypes.map(name => (
                          <Option key={name.label}>{name.value}</Option>
                        ))}
                  </Select>
                </Form.Item>
              )}
              {activeTab === 'EMAIL' && (
                <div>
                  <Form.Item
                    name="addDisclosure"
                    label="Add a disclosure?"
                    valuePropName="checked">
                    <Switch />
                  </Form.Item>

                  {addDisclosure && (
                    <div>
                      <Form.Item
                        rules={[
                          {
                            required: true,
                            message: 'Disclosure Type is required',
                          },
                        ]}
                        name="disclosureType"
                        label="Disclosure Type">
                        <Select
                          style={{width: 720}}
                          name="disclosureType"
                          value={disclosureType}
                          placeholder="Select"
                          onChange={e => onDisclosureType(e)}>
                          {disclosureTypeEnum.map(data => (
                            <Option key={data.value}>{data.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        rules={[
                          {
                            required: true,
                            message: 'Disclosure is required',
                          },
                        ]}
                        name="disclosure"
                        label="Disclosure">
                        <Select
                          style={{width: 720}}
                          name="disclosure"
                          placeholder="Select">
                          {getAllEmailDisclosures?.getAllEmailDisclosures?.disclosures.map(
                            data => (
                              <Option key={data.id}>{data.title}</Option>
                            ),
                          )}
                        </Select>
                      </Form.Item>
                    </div>
                  )}
                </div>
              )}
              {/** ONLY display External Link for push notifications */}
              {type === 'EXTERNAL_LINK' && (
                <>
                  <Form.Item
                    rules={[
                      {
                        required: type === 'EXTERNAL_LINK',
                        message: 'External link is required',
                        type: 'url',
                      },
                    ]}
                    label="External Link URL"
                    className={styles.inputField}
                    name="externalLink">
                    <Input placeholder="Please paste a valid External Link" />
                  </Form.Item>
                  <Form.Item
                    label="Should this link open in the App?"
                    className={styles.inputField}
                    name="linkShouldOpenInTheApp"
                    valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </>
              )}
              {type === 'DIVIDENDS_ANNOUNCEMENT' && (
                <>
                  <Form.Item
                    rules={[
                      {
                        required: true,
                        message: 'Asset type is required',
                      },
                    ]}
                    label="Asset type"
                    className={styles.inputField}
                    name="assetClass">
                    <Select
                      value={type}
                      placeholder="Select an asset type"
                      name="assetClass">
                      {assetClassEnum.map(name => (
                        <Option key={name.label}>{name.value}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    rules={[
                      {
                        required: type === 'DIVIDENDS_ANNOUNCEMENT',
                        message: 'Company is required',
                      },
                    ]}
                    label="Company"
                    className={styles.inputField}
                    name="company">
                    <Select
                      value={type}
                      placeholder="Select a company"
                      name="company">
                      {data?.getAllCompanies?.map(({id, name, logoImgURL}) => (
                        <Option value={`${name};${id}`} key={id}>
                          <div className={styles['all-data']}>
                            <img src={logoImgURL} alt="" />
                            <span>{name}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              )}
              <div>
                <PushNotificationData
                  type={type}
                  data={data}
                  form={form}
                  handleReportMetaData={setReportMetaData}
                  handleOpportunityMetaData={setOpportunityMetaData}
                  handleCompanyMetaData={setCompanyMetaData}
                  handleGroupMetaData={setGroupMetaData}
                />
              </div>
              {/* Any notification type may carry an announcement, so this is
              not scoped to a destination type. */}
              <RelatedAnnouncementPicker className={styles.inputField} />
            </div>
          </div>

          {activeTab === 'EMAIL' && (
            <div className={styles.section}>
              <div className={styles.sectionHead}>
                <span className={styles.sectionStep}>4</span>
                <h3>Sender</h3>
                <span>Who the email comes from</span>
              </div>
              <div className={styles.sectionBody}>
                <div className={styles['audience-grid']}>
                  <Form.Item
                    rules={[
                      {
                        required: true,
                        message: 'Sender Email required',
                      },
                    ]}
                    name="senderEmail"
                    label="Sender Email">
                    <Select
                      className={styles['fwidth']}
                      name="senderEmail"
                      placeholder="@">
                      {data?.getOfficialEmails.map(data => (
                        <Option key={data.email}>{data.email}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className={styles['audience-grid']}>
                  <Form.Item name="replyEmail" label="Reply To Email">
                    <Select
                      className={styles['fwidth']}
                      name="replyEmail"
                      placeholder="@">
                      {data?.getOfficialEmails.map(data => (
                        <Option key={data.email}>{data.email}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
            </div>
          )}

          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionStep}>
                {activeTab === 'EMAIL' ? '5' : '4'}
              </span>
              <h3>Audience</h3>
              <span>Who receives it</span>
            </div>
            <div className={styles.sectionBody}>
              <NotificationFilter
                audience={audience}
                setAudience={value => form.setFieldsValue({audience: value})}
                recipients={recipients}
                setRecipients={setRecipients}
                defaultUsers={defaultUserIds}
                allGroups={data?.getAllGroups?.groups}
                allCampaigns={data?.getAllCampaigns?.campaigns}
                allTopics={data?.getAllTopics}
                allCountries={getAllCountries?.getAllCountries}
                form={form}
                isEditMode={isEditMode}
              />
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <span className={styles.sectionStep}>
                {activeTab === 'EMAIL' ? '6' : '5'}
              </span>
              <h3>Schedule</h3>
              <span>When it goes out, and whether it repeats</span>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles['schedule-grid']}>
                <Form.Item
                  rules={[
                    {
                      required: true,
                      message: 'Schedule date is required',
                    },
                    {
                      validator: (_, value) => {
                        if (!value) {
                          // eslint-disable-next-line no-undef
                          return Promise.resolve();
                        }
                        const earliest = moment().add(
                          MIN_LEAD_MINUTES,
                          'minutes',
                        );
                        if (moment(value).isBefore(earliest)) {
                          // eslint-disable-next-line no-undef, prefer-promise-reject-errors
                          return Promise.reject(
                            new Error(
                              `Pick a time at least ${MIN_LEAD_MINUTES} minutes from now — earliest is ${earliest.format(
                                'HH:mm',
                              )}`,
                            ),
                          );
                        }
                        // eslint-disable-next-line no-undef
                        return Promise.resolve();
                      },
                    },
                  ]}
                  name="date"
                  label="Published Date"
                  extra={`Must be at least ${MIN_LEAD_MINUTES} minutes from now. Times are in ${getUserTimezone()}.`}>
                  <DatePicker
                    className={styles.date}
                    name="Date"
                    format={'YYYY-MM-DD HH:mm:ss'}
                    showTime={{
                      defaultValue: moment('00:00', 'HH:mm:ss'),
                    }}
                    disabledDate={current => {
                      return current && current < moment().startOf('day');
                    }}
                    disabledTime={current => {
                      if (!current) {
                        return {};
                      }
                      // The earliest valid send, not "now" — otherwise the
                      // picker offers minutes the validator then rejects.
                      const now = moment().add(MIN_LEAD_MINUTES, 'minutes');
                      if (current.isSame(now, 'day')) {
                        return {
                          disabledHours: () => {
                            const hours = [];
                            for (let i = 0; i < now.hour(); i++) {
                              hours.push(i);
                            }
                            return hours;
                          },
                          disabledMinutes: selectedHour => {
                            if (selectedHour === now.hour()) {
                              const minutes = [];
                              for (let i = 0; i <= now.minute(); i++) {
                                minutes.push(i);
                              }
                              return minutes;
                            }
                            return [];
                          },
                          disabledSeconds: (selectedHour, selectedMinute) => {
                            if (
                              selectedHour === now.hour() &&
                              selectedMinute === now.minute()
                            ) {
                              const seconds = [];
                              for (let i = 0; i <= now.second(); i++) {
                                seconds.push(i);
                              }
                              return seconds;
                            }
                            return [];
                          },
                        };
                      }
                      return {};
                    }}
                    renderExtraFooter={() => (
                      <div style={{textAlign: 'center'}}>
                        <p>Your timezone is: {getUserTimezone()}</p>
                      </div>
                    )}
                  />
                </Form.Item>
                {category.includes('IN_APP') && (
                  <Form.Item
                    name="expires"
                    label="Expiry Date"
                    extra={`Optional. When set, at least ${MIN_EXPIRY_HOURS} hours after the published date.`}
                    dependencies={['date']}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) {
                            // eslint-disable-next-line no-undef
                            return Promise.resolve();
                          }
                          const selectedDate = moment(value);
                          const now = moment();
                          const publishedDate = form.getFieldValue('date')
                            ? moment(form.getFieldValue('date'))
                            : null;

                          if (selectedDate.isBefore(now)) {
                            // eslint-disable-next-line no-undef, prefer-promise-reject-errors
                            return Promise.reject(
                              new Error('Expiry date cannot be in the past'),
                            );
                          }

                          // The backend requires a full day between the two;
                          // this only checked that expiry came second.
                          if (publishedDate) {
                            const earliest = publishedDate
                              .clone()
                              .add(MIN_EXPIRY_HOURS, 'hours');
                            if (selectedDate.isBefore(earliest)) {
                              // eslint-disable-next-line no-undef, prefer-promise-reject-errors
                              return Promise.reject(
                                new Error(
                                  `Expiry must be at least ${MIN_EXPIRY_HOURS} hours after the published date — earliest is ${earliest.format(
                                    'D MMM YYYY, HH:mm',
                                  )}`,
                                ),
                              );
                            }
                          }

                          // eslint-disable-next-line no-undef
                          return Promise.resolve();
                        },
                      },
                    ]}>
                    <DatePicker
                      className={styles.date}
                      name="expires"
                      format={'YYYY-MM-DD HH:mm:ss'}
                      showTime={{
                        defaultValue: moment('00:00', 'HH:mm:ss'),
                      }}
                      disabledDate={current => {
                        if (!current) {
                          return false;
                        }
                        const now = moment().startOf('day');
                        const publishedDate = form.getFieldValue('date')
                          ? moment(form.getFieldValue('date')).startOf('day')
                          : null;

                        // Disable dates before today
                        if (current < now) {
                          return true;
                        }

                        // Disable dates before published date if published date exists
                        if (publishedDate && current < publishedDate) {
                          return true;
                        }

                        return false;
                      }}
                      disabledTime={current => {
                        if (!current) {
                          return {};
                        }
                        const now = moment();
                        const publishedDate = form.getFieldValue('date')
                          ? moment(form.getFieldValue('date'))
                          : null;

                        // Use the later of now or published date for time restrictions
                        const referenceTime =
                          publishedDate && publishedDate.isAfter(now)
                            ? publishedDate
                            : now;

                        if (current.isSame(referenceTime, 'day')) {
                          return {
                            disabledHours: () => {
                              const hours = [];
                              for (let i = 0; i < referenceTime.hour(); i++) {
                                hours.push(i);
                              }
                              return hours;
                            },
                            disabledMinutes: selectedHour => {
                              if (selectedHour === referenceTime.hour()) {
                                const minutes = [];
                                for (
                                  let i = 0;
                                  i <= referenceTime.minute();
                                  i++
                                ) {
                                  minutes.push(i);
                                }
                                return minutes;
                              }
                              return [];
                            },
                            disabledSeconds: (selectedHour, selectedMinute) => {
                              if (
                                selectedHour === referenceTime.hour() &&
                                selectedMinute === referenceTime.minute()
                              ) {
                                const seconds = [];
                                for (
                                  let i = 0;
                                  i <= referenceTime.second();
                                  i++
                                ) {
                                  seconds.push(i);
                                }
                                return seconds;
                              }
                              return [];
                            },
                          };
                        }
                        return {};
                      }}
                      renderExtraFooter={() => (
                        <div style={{textAlign: 'center'}}>
                          <p>Your timezone is: {getUserTimezone()}</p>
                        </div>
                      )}
                    />
                  </Form.Item>
                )}
                <Form.Item
                  className={styles.date}
                  name="frequency"
                  label="Frequency">
                  <Select
                    className={styles.fwidth}
                    name="frequency"
                    placeholder="Repeated">
                    <Option value="REPEATED">Repeated</Option>
                    <Option value="ONE_TIME">One Time</Option>
                  </Select>
                </Form.Item>
                {frequency === 'REPEATED' && (
                  <Form.Item
                    className={styles.date}
                    name="selectedDays"
                    label="Selected Days">
                    <Select
                      mode="multiple"
                      name="selectedDays"
                      placeholder="Monday, Tuesday, Sunday">
                      {notificationOptions.map(name => (
                        <Option key={name.label} value={name.label}>
                          {name.value}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                {!isEditMode && (
                  <Form.Item
                    label="Status"
                    className={styles.date}
                    name="draft"
                    initialValue={false}>
                    <Select placeholder="Select Status">
                      <Option value={false}>Publish</Option>
                      <Option value={true}>Draft</Option>
                    </Select>
                  </Form.Item>
                )}
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.footerStatus}>
              Sending on{' '}
              <span className={styles.footerChannel}>
                {activeTab === 'APP'
                  ? (category ?? []).join(' + ') || 'push / in-app'
                  : activeTab === 'EMAIL'
                  ? 'email'
                  : 'WhatsApp'}
              </span>
              {form.getFieldValue('draft') ? (
                <>
                  {' '}
                  · <span className={styles.footerDraft}>saved as draft</span>
                </>
              ) : null}
            </div>
            <Form.Item name="button" style={{margin: 0}}>
              <SubmitButton
                disabled={disableSendButton}
                label={submitButtonLabel}
              />
            </Form.Item>
          </div>
        </Form>
      </div>
      <Modal
        open={openDeleteTranslationModal}
        onCancel={() => setOpenDeleteTranslationModal(false)}
        onOk={onDeleteTab}
        title="Delete Translation">
        Are you sure you want to delete this translation?
      </Modal>
    </div>
  );
};

NewNotification.modules = {
  toolbar: [
    [{header: '1'}, {header: '2'}, {header: [3, 4, 5, 6]}, {font: []}],
    [{size: []}],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{list: 'ordered'}, {list: 'bullet'}],
    ['link'],
    ['code-block'],
  ],
};

NewNotification.format = [
  'header',
  'font',
  'size',
  'strike',
  'bold',
  'italic',
  'underline',
  'blockquote',
  'list',
  'bullet',
  'link',
  'code-block',
];

export default NewNotification;
